<template>
    <div class="tile">
        <div class="tile-content">
            <div class="grid-content grid-unstackable">
                <div class="grid-item">
                    <div class="avatar avatar-description">
                        <div class="avatar-image" v-fs-block>
                            <figure class="image image-avatar">
                                <img :src="gatewayImageMap[gateway.gatewayName]"/>
                            </figure>
                        </div>
                        <div class="avatar-info" v-fs-block>
                            <p>{{gateway.gatewayName}}</p>
                            <p class="avatar-info-muted-text" v-fs-block>Created on {{gateway.createdTime | dateUTC('inlineDateValue')}}</p>
                        </div>
                    </div>
                </div>
                <div class="grid-item no-flex">Last updated {{gateway.updatedTime | dateUTC('inlineDateValue')}}
                </div>
            </div>
            <div class="grid-content">
                <div class="grid-item grid-item-full">
                    <r-badge v-if="isDown" type="negative" class="inline-xs">Ongoing downtime</r-badge>
                    <r-badge class="inline-xs" :type="getStatusClass">{{statusLabel}}</r-badge>
                    <r-badge v-if="hasDCC" class="inline-xs" type="info">DCC</r-badge>
                    <r-badge v-if="has3DS" class="inline-xs" type="info">{{threeDSecureLabel}}</r-badge>
                </div>
            </div>
        </div>
        <div class="tile-content" v-if="!requiresOnboarding">
            <div class="grid-content grid-unstackable">
                <div class="grid-item">Enable gateway account</div>
                <div class="grid-item no-flex">
                    <v-toggle
                        :value="true"
                        :selected="gateway.isActive"
                        :disabled="isProcessing || gateway.isClosed"
                        @input="toggleStatus"/>
                </div>
            </div>
        </div>
        <div v-if="!requiresOnboarding">
            <div class="tile-content">
                <div class="grid-content grid-unstackable">
                    <div class="grid-item">Close gateway account</div>
                    <div class="grid-item no-flex">
                        <button class="btn btn-s btn-danger"
                                :disabled="gateway.isClosed"
                                @click="closeGateway">Close</button>
                    </div>
                </div>
                <p class="toggle-warning">Closing this gateway account cannot be undone.</p>
            </div>
        </div>
        <div class="tile-content" v-else>
            <div class="message message-warning">
                <div class="message-content">
                    The onboarding process for <strong>{{gateway.gatewayName}}</strong> was not completed.
                    You will not be able to process payments through this gateway until resumed.
                    <r-button type="link" @click="resumeOnboarding">Resume onboarding now</r-button>.
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import _ from '@/common/helpers';
    import {vToggle} from '@/components/form';
    import GatewaysImageMap from '@/data/gateways-image-map';
    import detailsMixins from './details-mixins';
    import GatewayAccountModel from '@/common/models/gateway-account-model';
    import {createDialog, vConfirm} from '@/components/modal/dialogs';
    import BadgeColorMap from '@/data/badges-color-map';

    export default {
        name: 'gateway-status',
        mixins: [detailsMixins],
        components: {
            vToggle,
        },
        computed: {
            statusLabel() {
                return _.capitalize(this.gateway.status);
            },
            isDown() {
                return this.gateway.isDown;
            },
            hasDCC() {
                return this.gateway.dccMarkup !== null;
            },
            has3DS() {
                return this.gateway.threeDSecure === true;
            },
            threeDSecureLabel() {
                let label = `3DS`;
                if (this.gateway.threeDSecure) {
                    label = `3DS: ${this.gateway.mpi.name}`;
                }
                return label;
            },
            requiresOnboarding() {
                return this.gateway.originalStatus === 'pending';
            },
            getStatusClass() {
                return this.gateway.isActive
                    ? BadgeColorMap.status.active
                    : BadgeColorMap.status.inactive;
            },
        },
        data() {
            return {
                gatewayImageMap: {...GatewaysImageMap},
                isProcessing: false,
            };
        },
        methods: {
            toggleStatus(isActive) {
                this.$emit('update', {status: isActive ? GatewayAccountModel.Statuses.active : GatewayAccountModel.Statuses.inactive});
                (async () => {
                    try {
                        const method = isActive ? 'enable' : 'disable';
                        const params = {id: this.gateway.id};
                        this.isProcessing = true;
                        await this.$api(({gatewayAccounts}) => gatewayAccounts[method](params));
                        this.$toast.positive(`Gateway ${_.truncate(this.gateway.id, {dots: true})} was successfully ${method}d.`);
                        this.$emit('update-timeline');
                    } catch (error) {
                        this.$emit('update', {status: !isActive ? GatewayAccountModel.Statuses.active : GatewayAccountModel.Statuses.inactive});
                        this.$toast.fromError(error, 'Unable to toggle the status of this gateway at this time. Please try again later or contact support');
                    } finally {
                        this.isProcessing = false;
                    }
                })();
            },
            resumeOnboarding() {
                const url = this.gateway.getOnBoardingUrl();
                if (url) {
                    window.location = url;
                } else {
                    this.$toast.warning('Unable to complete the onboarding process at this time. Please try again later or contact support.');
                }
            },
            async closeGateway() {
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: 'Are you absolutely sure?',
                    content: 'Closing this gateway account cannot be undone!',
                    confirmLabel: 'Close',
                    dangerConfirm: true,
                };
                if (await confirmDialog(dialogParams)) {
                    const originalStatus = this.gateway.status;
                    const status = GatewayAccountModel.Statuses.closed;
                    this.$emit('update', {status});
                    try {
                        const params = {id: this.gateway.id};
                        this.isProcessing = true;
                        await this.$api(({gatewayAccounts}) => gatewayAccounts.close(params));
                        this.$emit('update-timeline');
                    } catch (error) {
                        this.$emit('update', {status: originalStatus});
                        this.$toast.fromError(error, 'Unable to archive the gateway at this time. Please try again later or contact support');
                    } finally {
                        this.isProcessing = false;
                    }
                }
            },
        },
    };
</script>

<style scoped>

</style>
